import { useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Alert,
  Tooltip,
} from "antd";
import {
  UserAddOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  FolderFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { VaultFolder, AccessPermission } from "@/types";
import {
  grantFolderAccess,
  checkUser,
  inviteGuestUser,
} from "@/services/storageManager";

const { Text } = Typography;

interface ShareFolderModalProps {
  folder: VaultFolder;
  open: boolean;
  onClose: () => void;
}

const DEFAULT_PERMISSION: AccessPermission = "download";

export default function ShareFolderModal({ folder, open, onClose }: ShareFolderModalProps) {
  const [form] = Form.useForm();
  const [isGranting, setIsGranting] = useState(false);
  const [userCheckStatus, setUserCheckStatus] = useState<{
    checked: boolean;
    exists: boolean;
    email: string;
  } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const handleGrant = async () => {
    try {
      const values = await form.validateFields();
      setIsGranting(true);

      await grantFolderAccess(folder.id, {
        grantedTo: values.email,
        permission: DEFAULT_PERMISSION,
        expiresAt: values.expiresAt?.toISOString(),
      });

      message.success(`Folder "${folder.name}" shared successfully`);
      form.resetFields();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to share folder";
      if (error.response?.data?.code === "GRANT_ALREADY_EXISTS") {
        message.warning("This user already has access to this folder");
      } else {
        message.error(errorMessage);
      }
    } finally {
      setIsGranting(false);
    }
  };

  const handleCheckUser = async () => {
    try {
      const values = await form.validateFields(["email"]);
      setIsChecking(true);

      const result = await checkUser(values.email);
      setUserCheckStatus({
        checked: true,
        exists: result.exists,
        email: values.email,
      });

      if (result.exists) {
        message.success("User found in Azure AD");
      } else {
        message.warning("User not found. You can invite them as a guest.");
      }
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || "Failed to check user");
    } finally {
      setIsChecking(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      const values = await form.validateFields(["email"]);
      setIsInviting(true);

      await inviteGuestUser({
        email: values.email,
        sendInvitationMessage: true,
        customMessage: `You have been invited to access the shared folder: "${folder.name}"`,
      });

      message.success(`Invitation sent to ${values.email}`);

      setUserCheckStatus({
        checked: true,
        exists: true,
        email: values.email,
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
  };

  const handleEmailChange = () => {
    setUserCheckStatus(null);
  };

  const handleClose = () => {
    form.resetFields();
    setUserCheckStatus(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <FolderFilled style={{ color: folder.color }} />
          {`Share folder "${folder.name}"`}
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      {/* Info about sharing */}
      <Alert
        type="info"
        showIcon
        icon={<ClockCircleOutlined />}
        message="Share entire folder"
        description="The recipient will see this folder in their 'Shared with me' section and can access all files within it."
        style={{ marginBottom: 20 }}
      />

      {/* Grant access form */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          expiresAt: dayjs().add(7, "day"),
        }}
      >
        <Form.Item
          name="email"
          label="Share with"
          rules={[
            { required: true, message: "Please enter an email address" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              prefix={<MailOutlined />}
              placeholder="user@example.com"
              size="large"
              onChange={handleEmailChange}
              disabled={isGranting}
              suffix={
                userCheckStatus?.checked && (
                  <Tooltip
                    title={
                      userCheckStatus.exists
                        ? "User exists in Azure AD"
                        : "User not found in Azure AD"
                    }
                  >
                    {userCheckStatus.exists ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                    )}
                  </Tooltip>
                )
              }
            />
            <Button
              size="large"
              onClick={handleCheckUser}
              loading={isChecking}
              disabled={isGranting}
            >
              Check
            </Button>
          </Space.Compact>
        </Form.Item>

        {/* Show invite option if user not found */}
        {userCheckStatus?.checked && !userCheckStatus.exists && (
          <Alert
            type="warning"
            showIcon
            message="User not found in Azure AD"
            description={
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>
                  This user is not part of your organization. You can invite them as a guest user.
                </Text>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleInviteUser}
                  loading={isInviting}
                  disabled={isGranting}
                  style={{ marginTop: 8 }}
                >
                  Send guest invitation
                </Button>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="expiresAt"
          label="Expires"
          rules={[
            { required: true, message: "Please select an expiration date" },
          ]}
        >
          <DatePicker
            size="large"
            style={{ width: "100%" }}
            disabled={isGranting}
            disabledDate={(current) => {
              const tomorrow = dayjs().add(1, "day").startOf("day");
              const maxDate = dayjs().add(30, "day");
              return current && (current < tomorrow || current > maxDate);
            }}
            format="MMM D, YYYY"
            placeholder="Select expiration date"
          />
        </Form.Item>

        <Tooltip
          title={
            !userCheckStatus?.checked
              ? "Please check if user exists first"
              : !userCheckStatus.exists
              ? "Please invite the user as a guest first"
              : ""
          }
        >
          <Button
            type="primary"
            onClick={handleGrant}
            loading={isGranting}
            icon={<UserAddOutlined />}
            size="large"
            style={{ marginTop: 16 }}
            disabled={
              !userCheckStatus?.checked ||
              !userCheckStatus.exists
            }
          >
            Share folder
          </Button>
        </Tooltip>
      </Form>
    </Modal>
  );
}
